import React, { useState, useEffect } from 'react';
import { getPendingUnlockRequests, unlockOptimization } from '../../services/optimizationService';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { verifyAdminAccess } from '../../services/adminService';

function UnlockRequests() {
  const { isAdmin } = useAdminAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unlocking, setUnlocking] = useState({});

  useEffect(() => {
    loadRequests();
    
    // Refresh every 10 seconds
    const interval = setInterval(loadRequests, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Verify admin access
      await verifyAdminAccess();
      
      const pendingRequests = await getPendingUnlockRequests();
      setRequests(pendingRequests);
    } catch (error) {
      console.error('Error loading unlock requests:', error);
      setError(error.message || 'Failed to load unlock requests');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (request) => {
    try {
      setUnlocking(prev => ({ ...prev, [request.id]: true }));
      
      // Get admin info
      const adminInfo = await verifyAdminAccess();
      
      // Use nextOptimization if available, otherwise use completedOptimization + 1
      const optimizationNumber = request.nextOptimization || 
        (request.completedOptimization ? request.completedOptimization + 1 : 1);
      
      await unlockOptimization(
        request.userId,
        optimizationNumber,
        adminInfo.adminId,
        adminInfo.adminEmail
      );
      
      // Remove from list
      setRequests(prev => prev.filter(req => req.id !== request.id));
      
      // Reload to get fresh data
      await loadRequests();
    } catch (error) {
      console.error('Error unlocking user:', error);
      alert(error.message || 'Failed to unlock user');
    } finally {
      setUnlocking(prev => ({ ...prev, [request.id]: false }));
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString('en-US');
    }
    return new Date(timestamp).toLocaleString('en-US');
  };

  if (!isAdmin) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Unauthorized: Admin access required
      </div>
    );
  }

  if (loading && requests.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading unlock requests...</p>
      </div>
    );
  }

  return (
    <div className="admin-unlock-requests">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-unlock me-2"></i>
            Pending Unlock Requests
          </h2>
          <p className="text-muted mb-0">
            Users waiting for optimization feature unlock
          </p>
        </div>
        <button 
          className="btn btn-outline-primary"
          onClick={loadRequests}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-2"></i>
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {requests.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-inbox fs-1 text-muted mb-3"></i>
            <h5 className="text-muted">No Pending Requests</h5>
            <p className="text-muted mb-0">All unlock requests have been processed.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>User Email</th>
                    <th>VIP Level</th>
                    <th>Completed Optimization</th>
                    <th>Next Optimization</th>
                    <th>Request Time</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => {
                    const nextOptimization = request.nextOptimization || 
                      (request.completedOptimization ? request.completedOptimization + 1 : 1);
                    return (
                      <tr key={request.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="bi bi-person-circle me-2 text-primary"></i>
                            <span>{request.userEmail || 'N/A'}</span>
                          </div>
                        </td>
                        <td>
                          <span className="badge bg-primary">{request.vipLevel || 'VIP1'}</span>
                        </td>
                        <td>
                          <span className="fw-bold">
                            {request.completedOptimization || request.completedSets || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-info">
                            Optimization {nextOptimization}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {formatDate(request.requestedAt)}
                          </small>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleUnlock(request)}
                            disabled={unlocking[request.id]}
                          >
                            {unlocking[request.id] ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Unlocking...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-unlock me-2"></i>
                                Unlock #{nextOptimization}
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UnlockRequests;

